"use client";

import { useEffect, useState, useCallback, useRef, memo, useMemo } from "react";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import { Connection, LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import { createUmiInstance } from "@/lib/umi";
import { fetchAllDigitalAssetWithTokenByOwner } from "@metaplex-foundation/mpl-token-metadata";
import { fromWeb3JsPublicKey } from "@metaplex-foundation/umi-web3js-adapters";
import { useOptimizedCNFTFetch } from "@/hooks/useOptimizedCNFTFetch";
import { Copy, Wallet, Loader2, Eye, EyeOff } from "lucide-react";
import { message } from "antd";
import { getRpcProxyUrl, getRpcWebSocketUrl } from "@/lib/rpc-url";

interface WalletInfoDisplayProps {
	showBalance?: boolean;
	showNFTCounts?: boolean;
	showFullAddress?: boolean;
	compact?: boolean;
	className?: string;
}

interface WalletData {
	balance: number;
	standardNFTCount: number;
	compressedNFTCount: number;
	totalNFTCount: number;
	isLoading: boolean;
}

// Inline Phantom logo so we don't depend on a remote asset. Sized by className.
const PhantomLogo = ({ className = "w-3.5 h-3.5" }: { className?: string }) => (
	<svg viewBox="0 0 128 128" className={className} aria-hidden="true">
		<defs>
			<linearGradient id="mgt-phantom-g" x1="64" y1="0" x2="64" y2="128" gradientUnits="userSpaceOnUse">
				<stop offset="0" stopColor="#534BB1" />
				<stop offset="1" stopColor="#551BF9" />
			</linearGradient>
		</defs>
		<rect width="128" height="128" rx="64" fill="url(#mgt-phantom-g)" />
		<path
			d="M110.584 64.9142H99.142C99.142 41.8335 80.173 23.1432 56.9573 23.1432C34.0266 23.1432 15.2858 41.4437 14.7723 64.3292C14.2369 88.0392 36.8699 108.571 60.5799 108.571H63.5999C84.4809 108.571 112.454 92.2679 116.833 72.4142C117.639 68.7608 114.332 64.9142 110.584 64.9142ZM39.7699 65.9426C39.7699 69.0571 37.2248 71.6022 34.1102 71.6022C30.9957 71.6022 28.4506 69.0571 28.4506 65.9426V56.7712C28.4506 53.6566 30.9957 51.1115 34.1102 51.1115C37.2248 51.1115 39.7699 53.6566 39.7699 56.7712V65.9426ZM59.4284 65.9426C59.4284 69.0571 56.8834 71.6022 53.7688 71.6022C50.6542 71.6022 48.1091 69.0571 48.1091 65.9426V56.7712C48.1091 53.6566 50.6542 51.1115 53.7688 51.1115C56.8834 51.1115 59.4284 53.6566 59.4284 56.7712V65.9426Z"
			fill="#FFFFFF"
		/>
	</svg>
);

const WalletInfoDisplay = memo(function WalletInfoDisplay({
	showBalance = true,
	showNFTCounts = true,
	showFullAddress = false,
	compact = false,
	className = "",
}: WalletInfoDisplayProps) {
	const {
		publicKey,
		signer,
		isConnected,
		authenticated,
		user,
		ready,
		walletType,
	} = usePrivyWallet() as any;

	// Privy returns walletClientType with varying casing (e.g. "Phantom" on
	// PC, "phantom" on mobile). Normalise before matching or the badge silently
	// fails to render.
	const normalizedWalletType = (walletType ?? "").toString().toLowerCase();

	const walletBadge = useMemo(() => {
		if (normalizedWalletType === "phantom") {
			return { label: "Phantom", className: "text-purple-300 bg-purple-500/15 border border-purple-400/30" };
		}
		if (normalizedWalletType === "solflare") {
			return { label: "Solflare", className: "text-orange-300 bg-orange-500/15 border border-orange-400/30" };
		}
		// Hide the internal Privy branding — show MGT Wallet to users.
		if (
			normalizedWalletType === "privy" ||
			normalizedWalletType === "smart_wallet" ||
			// Fall back to MGT Wallet for any other embedded/unknown type when
			// the user is connected, so we always show *something*.
			(isConnected && publicKey && !normalizedWalletType)
		) {
			return { label: "MGT Wallet", className: "text-mgt-green bg-mgt-green/10 border border-mgt-green/30" };
		}
		return null;
	}, [normalizedWalletType, isConnected, publicKey]);

	const [walletData, setWalletData] = useState<WalletData>({
		balance: 0,
		standardNFTCount: 0,
		compressedNFTCount: 0,
		totalNFTCount: 0,
		isLoading: false,
	});
	const [showFullAddr, setShowFullAddr] = useState(false);
	const [umi, setUmi] = useState<any>(null);
	const isFetchingRef = useRef(false);
	const lastFetchedKey = useRef<string | null>(null);
	const umiInitializedRef = useRef<string | null>(null);
	// Always points to the latest fetchWalletData — avoids stale closures in timers
	const fetchWalletDataRef = useRef<() => Promise<void>>(async () => {});

	// Memoize connection to prevent recreation
	const connection = useMemo(() => {
		// Use centralized RPC URL utility to ensure consistent URL construction
		const rpcUrl = getRpcProxyUrl();
		// Disable WebSocket since proxy only supports HTTP
		return new Connection(rpcUrl, {
			commitment: "confirmed",
			wsEndpoint: getRpcWebSocketUrl(),
		});
	}, []);

	// Memoize formatted address
	const formattedAddress = useMemo(() => {
		if (!publicKey) return "";
		if (showFullAddress || showFullAddr) return publicKey.toString();
		if (compact) {
			return `${publicKey.toString().slice(0, 4)}...${publicKey
				.toString()
				.slice(-4)}`;
		}
		return `${publicKey.toString().slice(0, 6)}...${publicKey
			.toString()
			.slice(-4)}`;
	}, [publicKey, showFullAddress, showFullAddr, compact]);

	// Memoize UMI signer to prevent recreation
	const umiSigner = useMemo(() => {
		if (!signer || !publicKey) return null;

		return {
			...signer,
			publicKey: publicKey as any,
			signAllTransactions: async (transactions: any[]) => {
				const signedTransactions = [];
				for (const transaction of transactions) {
					const signed = await signer.signTransaction(transaction);
					signedTransactions.push(signed);
				}
				return signedTransactions;
			},
		};
	}, [signer, publicKey]);

	// Initialize UMI only when signer or publicKey actually changes
	useEffect(() => {
		if (umiSigner && publicKey) {
			const walletKey = publicKey.toString();

			// Only initialize if we haven't already initialized for this wallet
			if (umiInitializedRef.current !== walletKey) {
				try {
					const umiInstance = createUmiInstance(umiSigner);
					setUmi(umiInstance);
					umiInitializedRef.current = walletKey;
				} catch (error) {
					console.error("Failed to initialize UMI for wallet info:", error);
					setUmi(null);
					umiInitializedRef.current = null;
				}
			}
		} else {
			setUmi(null);
			umiInitializedRef.current = null;
		}
	}, [umiSigner, publicKey]); // Include publicKey to track wallet changes

	// Metadata extraction helper
	const safelyExtractMetadata = useCallback(
		async (item: any): Promise<any | null> => {
			try {
				if (!item || !item.id || !item.content) return null;
				if (!item.compression || !item.compression.compressed) return null;

				const metadataFromResponse = item.content?.metadata || {};
				const imageUri =
					item.content?.links?.image ||
					item.content?.files?.[0]?.uri ||
					item.content?.files?.[0]?.cdn_uri ||
					"";
				const safeName =
					metadataFromResponse.name || `Compressed NFT #${item.id.slice(0, 8)}`;

				return {
					publicKey: item.id,
					isCompressed: Boolean(item.compression?.compressed),
					metadata_uri: {
						name: safeName,
						image: imageUri,
						publicKey: item.id,
						owner: item.ownership?.owner || publicKey?.toString() || "",
						mint: item.id,
						updateAuthority: item.authorities?.[0]?.address || "",
						attributes: metadataFromResponse.attributes || [],
					},
					mint: { publicKey: item.id },
					metadata: { uri: item.content?.json_uri || "" },
					token: null,
				};
			} catch (error) {
				return null;
			}
		},
		[publicKey]
	);

	const addUniqueTraits = useCallback((traits: any[]) => {
		// Simple stub for traits - not needed for wallet info display
	}, []);

	// Use optimized CNFT fetch hook with silent mode to prevent messages
	const { fetchCompressedNFTs: optimizedFetchCNFTs, cancelFetch: cancelCNFTFetch } = useOptimizedCNFTFetch(
		safelyExtractMetadata,
		addUniqueTraits,
		true // silent mode - no messages
	);

	useEffect(() => {
		return () => {
			cancelCNFTFetch();
		};
	}, [cancelCNFTFetch]);

	// Fetch wallet balance
	const fetchBalance = useCallback(async (): Promise<number> => {
		if (!publicKey) return 0;
		try {
			const balanceInLamports = await connection.getBalance(
				new PublicKey(publicKey)
			);
			return balanceInLamports / LAMPORTS_PER_SOL;
		} catch (error) {
			console.error("Balance fetch error:", error);
			return 0;
		}
	}, [publicKey, connection]);

	// Fetch standard NFTs count
	const fetchStandardNFTCount = useCallback(async (): Promise<number> => {
		if (!publicKey || !umi) return 0;
		try {
			const fetchedStandardNFTs = await fetchAllDigitalAssetWithTokenByOwner(
				umi,
				fromWeb3JsPublicKey(new PublicKey(publicKey))
			);
			return fetchedStandardNFTs.length;
		} catch (error) {
			console.error("Standard NFT count fetch error:", error);
			return 0;
		}
	}, [publicKey, umi]);

	// Fetch compressed NFTs count (silently - no UI messages)
	const fetchCompressedNFTCount = useCallback(async (): Promise<number> => {
		if (!publicKey) return 0;
		try {
			// Clear any existing messages before silent fetch
			message.destroy();
			// Pass true to suppress messages
			const compressedNFTs = await optimizedFetchCNFTs(true);
			// Clear messages again to prevent showing any accidental messages
			setTimeout(() => message.destroy(), 100);
			return compressedNFTs.length;
		} catch (error) {
			console.error("Compressed NFT count fetch error:", error);
			// Clear any error messages too
			message.destroy();
			return 0;
		}
	}, [publicKey, optimizedFetchCNFTs]);

	// Fetch all wallet data
	const fetchWalletData = useCallback(async () => {
		if (!isConnected || !publicKey) return;

		// Prevent multiple simultaneous fetches for the same wallet
		const currentKey = publicKey.toString();
		if (isFetchingRef.current || lastFetchedKey.current === currentKey) {
			return;
		}

		isFetchingRef.current = true;
		lastFetchedKey.current = currentKey;
		setWalletData((prev) => ({ ...prev, isLoading: true }));

		try {
			const [balance, standardCount, compressedCount] = await Promise.all([
				showBalance ? fetchBalance() : Promise.resolve(0),
				showNFTCounts ? fetchStandardNFTCount() : Promise.resolve(0),
				showNFTCounts ? fetchCompressedNFTCount() : Promise.resolve(0),
			]);

			setWalletData({
				balance,
				standardNFTCount: standardCount,
				compressedNFTCount: compressedCount,
				totalNFTCount: standardCount + compressedCount,
				isLoading: false,
			});
		} catch (error) {
			console.error("Error fetching wallet data:", error);
			setWalletData((prev) => ({ ...prev, isLoading: false }));
		} finally {
			isFetchingRef.current = false;
		}
	}, [
		isConnected,
		publicKey,
		showBalance,
		showNFTCounts,
		fetchBalance,
		fetchStandardNFTCount,
		fetchCompressedNFTCount,
	]);

	// Keep ref in sync with latest fetchWalletData to avoid stale closures in timers
	useEffect(() => {
		fetchWalletDataRef.current = fetchWalletData;
	}, [fetchWalletData]);

	// Reset fetch tracking when wallet changes, and immediately clear displayed data
	useEffect(() => {
		if (!publicKey) return;
		const keyStr = publicKey.toString();
		if (lastFetchedKey.current !== null && lastFetchedKey.current !== keyStr) {
			// Wallet switched — clear stale balances/counts immediately
			isFetchingRef.current = false;
			setWalletData({
				balance: 0,
				standardNFTCount: 0,
				compressedNFTCount: 0,
				totalNFTCount: 0,
				isLoading: true,
			});
		}
		lastFetchedKey.current = null; // Allow fresh fetch
	}, [publicKey]);

	// Initial fetch - only when wallet connection changes (NOT when fetchWalletData changes)
	useEffect(() => {
		if (isConnected && publicKey && !isFetchingRef.current) {
			// Add a small delay to prevent UI blocking on menu open
			const timer = setTimeout(() => {
				const currentKey = publicKey.toString();
				if (lastFetchedKey.current === currentKey) {
					return; // Already fetched for this wallet
				}
				// Use ref to always call the latest fetchWalletData (avoids stale closure)
				const doFetch = async () => {
					try {
						await fetchWalletDataRef.current();
					} catch (error) {
						console.error("Error in wallet data fetch:", error);
						// Fail silently - don't let errors affect the UI
					}
				};
				doFetch();
			}, 300); // Small delay to let the menu render first

			return () => clearTimeout(timer);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isConnected, publicKey]); // Only depend on connection state and wallet key

	// Copy address to clipboard
	const handleCopyAddress = async () => {
		if (publicKey) {
			try {
				await navigator.clipboard.writeText(publicKey.toString());
				message.success("Address copied to clipboard!");
			} catch (error) {
				console.error("Failed to copy address:", error);
				message.error("Failed to copy address");
			}
		}
	};

	// Format address display
	const formatAddress = (address: string, full: boolean = false): string => {
		if (!address) return "";
		if (full) return address;
		if (compact) {
			return `${address.slice(0, 4)}...${address.slice(-4)}`;
		}
		return `${address.slice(0, 6)}...${address.slice(-4)}`;
	};

	if (!isConnected || !publicKey) {
		return (
			<div className={`text-gray-400 text-xs ${className}`}>
				Wallet not connected
			</div>
		);
	}

	return (
		<div className={`text-white ${className}`}>
			{compact ? (
				// Compact display for navbar
				<div className="flex items-center space-x-3">
					{/* Connection indicator */}
					<div className="flex items-center space-x-2">
						<div className="w-2 h-2 bg-green-500 rounded-full"></div>
						<div className="flex flex-col">
							{/* Wallet type badge — so users clearly see whether Phantom,
							    Solflare, or their MGT Wallet is connected. */}
							{walletBadge && (
								<span
									className={`inline-flex items-center gap-1 self-start text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md mb-1 ${walletBadge.className}`}
									title={`Connected via ${walletBadge.label}`}
								>
									{normalizedWalletType === "phantom" && <PhantomLogo className="w-3 h-3" />}
									{walletBadge.label}
								</span>
							)}
							{/* Address */}
							<div className="flex items-center space-x-1">
								<button
									type="button"
									aria-label={showFullAddr ? "Hide full address" : "Show full address"}
									className="text-xs font-mono text-gray-300 cursor-pointer hover:text-white transition-colors"
									onClick={() => setShowFullAddr(!showFullAddr)}
								>
									{formattedAddress}
								</button>
								<button
									onClick={handleCopyAddress}
									aria-label="Copy wallet address"
									className="p-0.5 hover:bg-white/10 rounded transition-colors"
									title="Copy address"
								>
									<Copy className="w-3 h-3 text-gray-400 hover:text-white" />
								</button>
								{!showFullAddress && (
									<button
										onClick={() => setShowFullAddr(!showFullAddr)}
										aria-label={
											showFullAddr ? "Hide full address" : "Show full address"
										}
										className="p-0.5 hover:bg-white/10 rounded transition-colors"
										title={
											showFullAddr ? "Hide full address" : "Show full address"
										}
									>
										{showFullAddr ? (
											<EyeOff className="w-3 h-3 text-gray-400 hover:text-white" />
										) : (
											<Eye className="w-3 h-3 text-gray-400 hover:text-white" />
										)}
									</button>
								)}
							</div>

							{/* Wallet stats */}
							<div className="flex items-center space-x-3 text-xs">
								{showBalance && (
									<span className="text-green-400">
										{walletData.isLoading ? (
											<Loader2 className="w-3 h-3 animate-spin inline" />
										) : (
											`${walletData.balance.toFixed(3)} SOL`
										)}
									</span>
								)}
								{showNFTCounts && (
									<span className="text-blue-400">
										{walletData.isLoading ? (
											<Loader2 className="w-3 h-3 animate-spin inline" />
										) : (
											`${walletData.totalNFTCount} NFTs`
										)}
									</span>
								)}
							</div>
						</div>
					</div>
				</div>
			) : (
				// Full display for wallet page or detailed views
				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<h3 className="text-base font-semibold flex items-center space-x-2 text-white">
							<Wallet className="w-5 h-5 text-mgt-green" />
							<span>Wallet Info</span>
							{walletBadge && (
								<span
									className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md ${walletBadge.className}`}
									title={`Connected via ${walletBadge.label}`}
								>
									{normalizedWalletType === "phantom" && <PhantomLogo className="w-3 h-3" />}
									{walletBadge.label}
								</span>
							)}
						</h3>
						{walletData.isLoading && (
							<Loader2 className="w-4 h-4 animate-spin text-mgt-green" />
						)}
					</div>

					{/* Address */}
					<div className="space-y-2">
						<label className="text-xs text-gray-400 font-medium">Address</label>
						<div className="flex items-center justify-between gap-2 bg-gray-900/80 px-3 py-2.5 rounded-lg border border-gray-700/50">
							<span className="text-sm font-mono text-white tracking-wide flex-1 overflow-hidden text-ellipsis">
								{formattedAddress}
							</span>
							<div className="flex items-center gap-1 flex-shrink-0">
								<button
									onClick={handleCopyAddress}
									aria-label="Copy wallet address"
									className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
									title="Copy address"
								>
									<Copy className="w-4 h-4 text-gray-400 hover:text-mgt-green" />
								</button>
								{!showFullAddress && (
									<button
										onClick={() => setShowFullAddr(!showFullAddr)}
										aria-label={
											showFullAddr ? "Hide full address" : "Show full address"
										}
										className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
										title={
											showFullAddr ? "Hide full address" : "Show full address"
										}
									>
										{showFullAddr ? (
											<EyeOff className="w-4 h-4 text-gray-400 hover:text-mgt-green" />
										) : (
											<Eye className="w-4 h-4 text-gray-400 hover:text-mgt-green" />
										)}
									</button>
								)}
							</div>
						</div>
					</div>

					{/* Balance */}
					{showBalance && (
						<div className="space-y-2">
							<label className="text-xs text-gray-400 font-medium">
								Balance
							</label>
							<div className="text-2xl font-bold text-mgt-green">
								{walletData.isLoading ? (
									<Loader2 className="w-6 h-6 animate-spin" />
								) : (
									`${walletData.balance.toFixed(4)} SOL`
								)}
							</div>
						</div>
					)}

					{/* NFT Counts */}
					{showNFTCounts && (
						<div className="grid grid-cols-3 gap-2.5">
							<div className="flex flex-col items-center justify-center p-3 bg-[#2d2829] rounded-lg border border-gray-700/50 min-h-[85px]">
								<div className="text-2xl font-bold text-blue-400 mb-1">
									{walletData.isLoading ? "..." : walletData.standardNFTCount}
								</div>
								<div className="text-[10px] text-gray-400 text-center leading-tight font-medium">
									Standard NFTs
								</div>
							</div>
							<div className="flex flex-col items-center justify-center p-3 bg-[#2d2829] rounded-lg border border-gray-700/50 min-h-[85px]">
								<div className="text-2xl font-bold text-purple-400 mb-1">
									{walletData.isLoading ? "..." : walletData.compressedNFTCount}
								</div>
								<div className="text-[10px] text-gray-400 text-center leading-tight font-medium">
									Compressed NFTs
								</div>
							</div>
							<div className="flex flex-col items-center justify-center p-3 bg-[#2d2829] rounded-lg border border-gray-700/50 min-h-[85px]">
								<div className="text-2xl font-bold text-yellow-400 mb-1">
									{walletData.isLoading ? "..." : walletData.totalNFTCount}
								</div>
								<div className="text-[10px] text-gray-400 text-center leading-tight font-medium">
									Total NFTs
								</div>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
});

export { WalletInfoDisplay };
